import { Routes } from '@angular/router';
import { EnhancedAuthGuard } from 'src/app/core/guards/enhanced-auth.guard';
import { PersonalSummaryTaskRedirectGuard } from 'src/app/core/guards/personal-summary-task-redirect.guard';
import { SmartRedirectGuard } from 'src/app/core/guards/smart-redirect.guard';
import { UserContextResolverService } from 'src/app/core/guards/user-context-resolver.service';
import { DashboardResolverService } from 'src/app/core/guards/dashboard-resolver.service';
import { RouteData } from 'src/app/core/models/route-data';

/**
 * Dashboard and entity record routes. Grouped here because they share the dashboard
 * resolver and use nested custom outlets (entityContent, carrierProfileContent) owned
 * by EntityRecordComponent and CarrierProfileComponent respectively.
 */
export const DASHBOARD_ROUTES: Routes = [
    {
        path: 'dashboard',
        loadComponent: () => import('src/components/entity-record/entity-record.component').then(m => m.EntityRecordComponent),
        canActivate: [EnhancedAuthGuard],
        resolve: {
            userContext: UserContextResolverService,
            dashboardData: DashboardResolverService
        },
        data: {
            requiresPermission: false // Dashboard itself is accessible, tabs have permission checks
        } satisfies RouteData,
        children: [
            // Default redirect when navigating to /dashboard with no child route
            {
                path: '',
                canActivate: [SmartRedirectGuard],
                data: {
                    redirectType: 'dashboard'
                } satisfies RouteData
            },
            // DASHBOARD ACTIVITY ROUTES
            {
                path: 'activity',
                loadComponent: () => import('src/components/entity-record/activity/activity.component').then(m => m.ActivityComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false,
                    // Firm-specific article ID 5 existed in pre-FOF-7854 determineHelpPathForFirm(), which
                    // was never called — firm users have always been served the advisors article. See FOF-7854.
                    helpId: { advisors: 6 }
                } satisfies RouteData
            },
            {
                path: 'allActivity',
                loadComponent: () => import('src/components/all-activity/all-activity-list.component').then(m => m.AllActivityListComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardAllActivity',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'globalActivities',
                loadComponent: () => import('src/components/all-dashboard-activity/all-dashboard-activity.component').then(m => m.AllDashboardActivityComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardGlobalActivities',
                    requiresPermission: true
                } satisfies RouteData
            },

            // DASHBOARD TAB ROUTES
            {
                path: 'compliance',
                loadComponent: () => import('src/components/carrier-producer-compliance-overview/compliance-overview.component').then(m => m.ComplianceOverviewComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardCompliance',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'producers',
                loadComponent: () => import('src/components/producer-onboarding/generic-recruiter-onboarding/generic-recruiter-onboarding.component').then(m => m.GenericRecruiterOnboardingComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'educationServices',
                loadComponent: () => import('src/components/education-services/education-services.component').then(m => m.EducationServicesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardEducationServices',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'disclosures',
                loadComponent: () => import('src/components/entity-record/disclosures/disclosures-list.component').then(m => m.DisclosuresListComponent),
                canActivate: [EnhancedAuthGuard],
                resolve: {
                    // Original resolver: fromInterview = (stateService.current.name === 'interview')
                    // Migrated: Component should check if coming from interview route via ActivatedRoute
                },
                data: {
                    permissionKey: 'dashboardDisclosures',
                    requiresPermission: true,
                    helpId: { advisors: 17 }
                } satisfies RouteData
            },
            {
                path: 'framedTransaction',
                loadComponent: () => import('src/components/framed-transaction/framed-transaction.component').then(m => m.FramedTransactionComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardFramedTransaction',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'licenses',
                loadComponent: () => import('src/components/licenses/licenses-v2.component').then(m => m.LicensesV2Component),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardLicenses',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'stateNotices',
                loadComponent: () => import('src/components/state-notices/state-notices.component').then(m => m.StateNoticesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardStateNotices',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'registrations',
                loadComponent: () => import('src/components/entity-record/registrations/registrations.component').then(m => m.RegistrationsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardRegistrations',
                    requiresPermission: true,
                    helpId: { advisors: 39 }
                } satisfies RouteData
            },
            {
                path: 'employments',
                loadComponent: () => import('src/components/entity-record/employments/employments.component').then(m => m.EmploymentsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardEmployments',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'allActions',
                loadComponent: () => import('src/components/all-actions/all-actions.component').then(m => m.AllActionsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardAllActions',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'reportsAndActivities',
                loadComponent: () => import('src/components/reports-and-activities/reports-and-activities.component').then(m => m.ReportsAndActivitiesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardReportsAndActivities',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'insurance',
                loadComponent: () => import('src/components/entity-record/insurance-securities-dashboards/insurance-dashboard.component').then(m => m.InsuranceDashboardComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardInsurance',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'securities',
                loadComponent: () => import('src/components/entity-record/insurance-securities-dashboards/securities-dashboard.component').then(m => m.SecuritiesDashboardComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardSecurities',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'compensation',
                loadComponent: () => import('src/components/compensation-dashboard/compensation-policies/compensation-policies.component').then(m => m.CompensationPoliciesComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardCompensation',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'qualifications',
                loadComponent: () => import('src/components/entity-record/qualifications/qualifications.component').then(m => m.QualificationsComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false // No permission check in original
                } satisfies RouteData
            },
            {
                path: 'contacts',
                loadComponent: () => import('src/components/contact-management/contact-management/contact-management.component').then(m => m.ContactManagementComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    // Original had plural-typo `requiresPermissionS` (never read by the guard) = NOT gated
                    // in production. Kept ungated to preserve behavior; "correcting" to true regressed it
                    // to an unauthorized Sad Cloud. See QUERY_PARAM_SERIALIZATION_REGRESSIONS.md.
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'carrierRequest',
                loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardCarrierRequest',
                    requiresPermission: true
                } satisfies RouteData
            },
            {
                path: 'relationship',
                loadComponent: () => import('src/components/selling-relationship/selling-relationship-validity-report/selling-relationship-validity-report.component').then(m => m.SellingRelationshipValidityReportComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardRelationship',
                    requiresPermission: true
                } satisfies RouteData
            },

            // DASHBOARD PAYMENT & TASK REDIRECT ROUTES
            {
                path: 'paymentPreferenceTaskRedirect',
                loadComponent: () => import('src/components/personal-summary/task-redirect/task-redirect.component').then(m => m.TaskRedirectComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'paymentPreferenceNotificationRedirect',
                loadComponent: () => import('src/components/personal-summary/task-redirect/task-redirect.component').then(m => m.TaskRedirectComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'paymentPreferenceEmailRedirect',
                loadComponent: () => import('src/components/personal-summary/task-redirect/task-redirect.component').then(m => m.TaskRedirectComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'policyTaskRedirect',
                loadComponent: () => import('src/components/personal-summary/task-redirect/task-redirect.component').then(m => m.TaskRedirectComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    requiresPermission: false
                } satisfies RouteData
            },

            // DASHBOARD PERSONAL SUMMARY ROUTES
            {
                path: 'personalSummary',
                loadComponent: () => import('src/components/personal-summary/personal-summary.component').then(m => m.PersonalSummaryComponent),
                // PersonalSummaryTaskRedirectGuard forwards `/personalSummary?taskId=...` to
                // policyTaskRedirect (UI-Router disambiguated these two states by query-param
                // presence; Angular can't). Ordered after auth so login redirect runs first.
                canActivate: [EnhancedAuthGuard, PersonalSummaryTaskRedirectGuard],
                data: {
                    // Original had plural-typo `requiresPermissionS` (never read by the guard) = NOT gated
                    // in production. Kept ungated to preserve behavior; "correcting" to true regressed it
                    // to an unauthorized Sad Cloud. See QUERY_PARAM_SERIALIZATION_REGRESSIONS.md.
                    requiresPermission: false
                } satisfies RouteData
            },
            {
                path: 'personalSummaryScheduler',
                loadComponent: () => import('src/components/personal-summary/personal-summary.component').then(m => m.PersonalSummaryComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    // Original had plural-typo `requiresPermissionS` (never read by the guard) = NOT gated
                    // in production. Kept ungated to preserve behavior; "correcting" to true regressed it
                    // to an unauthorized Sad Cloud. See QUERY_PARAM_SERIALIZATION_REGRESSIONS.md.
                    requiresPermission: false
                } satisfies RouteData
            },

            // DASHBOARD CARRIER PROFILE ROUTES
            {
                path: 'carrierProfile',
                loadComponent: () => import('src/components/carrier-profile/carrier-profile.component').then(m => m.CarrierProfileComponent),
                canActivate: [EnhancedAuthGuard],
                data: {
                    permissionKey: 'dashboardCarrierProfile',
                    requiresPermission: true
                } satisfies RouteData,
                children: [
                    {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'activity'
                    },
                    {
                        path: 'activity',
                        loadComponent: () => import('src/components/carrier-profile/activity/carrier-profile-activity.component').then(m => m.CarrierProfileActivityComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'dashboardCarrierProfileActivity',
                            requiresPermission: true
                        } satisfies RouteData,
                        children: [
                            {
                                path: 'producerContracts',
                                loadComponent: () => import('src/components/carrier-profile/activity/carrier-profile-activity.component').then(m => m.CarrierProfileActivityComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    requiresPermission: false // No permission check in original
                                } satisfies RouteData
                            },
                            {
                                path: 'beneficiaries',
                                loadComponent: () => import('src/components/carrier-profile/activity/carrier-profile-activity.component').then(m => m.CarrierProfileActivityComponent),
                                canActivate: [EnhancedAuthGuard],
                                data: {
                                    requiresPermission: false // No permission check in original
                                } satisfies RouteData
                            }
                        ]
                    },
                    {
                        path: 'hierarchyDownline',
                        loadComponent: () => import('src/components/carrier-profile/producer-hierarchy/carrier-profile-producer-hierarchy.component').then(m => m.CarrierProfileProducerHierarchyComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'dashboardCarrierProfileHierarchyDownline',
                            requiresPermission: true
                        } satisfies RouteData
                    },
                    {
                        path: 'downlineCarrierRequest',
                        loadComponent: () => import('src/components/carrier-request/carrier-request.component').then(m => m.CarrierRequestComponent),
                        canActivate: [EnhancedAuthGuard],
                        data: {
                            permissionKey: 'dashboardCarrierProfileDownlineCarrierRequest',
                            requiresPermission: true
                        } satisfies RouteData
                    }
                ]
            }
        ]
    }
];
